/**
 * SubMenuBar Component - Booku V2
 *
 * Bar horizontal untuk navigasi sub-menu multi-level.
 * Muncul di bawah AppTabBar saat user klik menu utama yang punya children.
 *
 * Fitur:
 * - Back button untuk kembali ke level sebelumnya
 * - Breadcrumb menampilkan path navigasi
 * - Menu items sebagai button/chip horizontal
 * - Arrow indicator (→) untuk item branch yang punya children
 * - Close button (×) untuk menutup bar
 * - Slide down animation saat muncul
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, X, ChevronRight } from 'lucide-react';
import { useSubMenuStore, useSubMenuBreadcrumb, useCanNavigateBack } from '@/stores/subMenuStore';
import { isMenuLeaf, isMenuBranch, type MenuConfigItem } from '@/config/menuConfig';
import { useAppTabsStore } from '@/stores/appTabsStore';
import { getPageByPath } from './PageRegistry';
import './SubMenuBar.css';

/** Durasi animasi dalam ms - harus sesuai dengan CSS */
const ANIMATION_DURATION = 333;

/**
 * Menentukan warna class berdasarkan ID menu.
 * Warna disesuaikan dengan fungsi/kategori menu.
 */
function getMenuColorClass(menuId: string): string {
  const id = menuId.toLowerCase();

  // ============================================
  // PEMBELIAN & PENJUALAN
  // ============================================

  // Invoice/Faktur - Oranye (dokumen tagihan)
  if (id.includes('invoice') || id.includes('faktur')) return 'color-invoice';

  // PO (Purchase Order/Sales Order) - Kuning (dokumen draft/awal)
  if (id.includes('po-')) return 'color-po';

  // Surat Jalan & BAST - Teal (dokumen pengiriman)
  if (id.includes('surat-jalan') || id.includes('bast')) return 'color-delivery';

  // Retur - Merah muda (pengembalian)
  if (id.includes('retur')) return 'color-retur';

  // Penjualan - Hijau cerah (pendapatan)
  if (id.includes('penjualan') || id.includes('jual') || id.includes('eceran')) return 'color-penjualan';

  // Pembelian - Biru kehijauan (pengeluaran produktif)
  if (id.includes('pembelian') || id.includes('beli')) return 'color-pembelian';

  // ============================================
  // TRANSAKSI
  // ============================================

  // Transaksi IN - Hijau (pemasukan)
  if (id.includes('transaksi-in')) return 'color-transaksi-in';

  // Transaksi OUT - Merah/Orange (pengeluaran)
  if (id.includes('transaksi-out')) return 'color-transaksi-out';

  // ============================================
  // BUKU PENGAWASAN
  // ============================================

  // Hutang - Merah/Orange (kewajiban)
  if (id.includes('hutang')) return 'color-hutang';

  // Piutang - Hijau (aset)
  if (id.includes('piutang')) return 'color-piutang';

  // Bank, Kas, Cash - Biru (keuangan)
  if (id.includes('bank') || id.includes('kas') || id.includes('cash')) return 'color-bank';

  // Gaji + Turunan Gaji (BPJS, Koperasi, Serikat) - Ungu (HR/payroll)
  if (id.includes('gaji') || id.includes('bpjs') || id.includes('koperasi') || id.includes('serikat')) return 'color-gaji';

  // Penerimaan - Teal (pemasukan)
  if (id.includes('penerimaan') || id.includes('pendapatan')) return 'color-penerimaan';

  // Pengeluaran, Bundel - Orange (pengeluaran)
  if (id.includes('pengeluaran') || id.includes('biaya') || id.includes('bundel')) return 'color-pengeluaran';

  // Pemindahbukuan - Kuning (transfer)
  if (id.includes('pemindah') || id.includes('transfer')) return 'color-transfer';

  // Aktiva, Asset - Cyan (aset tetap)
  if (id.includes('aktiva') || id.includes('asset') || id.includes('aset')) return 'color-aktiva';

  // Deposit / Reimbursement (eks Deposit Operasional) - Biru muda
  if (id.includes('deposit') || id.includes('reimbursement')) return 'color-deposit';

  // Garansi - Ungu muda
  if (id.includes('garansi')) return 'color-garansi';

  // Dividen - Emas
  if (id.includes('dividen')) return 'color-dividen';

  // ============================================
  // PAJAK
  // ============================================

  // Pajak - Merah gelap
  if (id.includes('pajak') || id.includes('pph') || id.includes('ppn') || id.includes('bukti-potong')) return 'color-pajak';

  // Equalisasi, Profil Pajak - Merah muda gelap
  if (id.includes('equalisasi') || id.includes('profil')) return 'color-pajak-admin';

  // ============================================
  // STOCK & MANAJEMEN ASSET
  // ============================================

  // Stock Opname - Coklat (persediaan)
  if (id.includes('stock') || id.includes('stok') || id.includes('opname') || id.includes('bahan') || id.includes('barang')) return 'color-stock';

  // Penyusutan - Ungu tua (depresiasi)
  if (id.includes('penyusutan') || id.includes('disposal')) return 'color-depresiasi';

  // Amortisasi - Ungu sedang
  if (id.includes('amortisasi')) return 'color-amortisasi';

  // ============================================
  // DATA MASTER
  // ============================================

  // COA, Akun - Indigo (data master)
  if (id.includes('coa') || id.includes('akun')) return 'color-coa';

  // User, Karyawan - Ungu muda (SDM)
  if (id.includes('user') || id.includes('karyawan')) return 'color-sdm';

  // Toko, Relasi - Coklat muda (mitra)
  if (id.includes('toko') || id.includes('relasi') || id.includes('lawan')) return 'color-mitra';

  // Project - Biru slate
  if (id.includes('project') || id.includes('proyek')) return 'color-project';

  // Pemegang Saham - Emas
  if (id.includes('saham') || id.includes('pemegang')) return 'color-saham';

  // Afiliasi - Biru Keunguan (relasi bisnis terkait)
  if (id.includes('afiliasi')) return 'color-afiliasi';

  // Pihak Ketiga - Abu-abu Biru (pihak eksternal)
  if (id.includes('pihak-ketiga')) return 'color-pihak-ketiga';

  // ============================================
  // JURNAL & LAPORAN
  // ============================================

  // Adjusment - Ungu tua (penyesuaian)
  if (id.includes('adjusment') || id.includes('penyesuaian') || id.includes('forex') || id.includes('hpp')) return 'color-adjusment';

  // Jurnal - Hijau tua (pembukuan)
  if (id.includes('jurnal')) return 'color-jurnal';

  // Laporan - Biru tua (reporting)
  if (id.includes('laporan') || id.includes('neraca') || id.includes('laba') || id.includes('buku-besar') || id.includes('aktivitas')) return 'color-laporan';

  // ============================================
  // DEFAULT
  // ============================================

  // Default - Abu-abu
  return 'color-default';
}

export function SubMenuBar() {
  const navigate = useNavigate();
  const { isOpen, currentItems, navigateToChild, navigateBack, closeSubMenu } = useSubMenuStore();
  const breadcrumb = useSubMenuBreadcrumb();
  const canGoBack = useCanNavigateBack();
  const { openTab } = useAppTabsStore();

  // State untuk animasi
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const prevIsOpen = useRef(isOpen);

  // Handle open/close dengan animasi
  useEffect(() => {
    if (isOpen && !prevIsOpen.current) {
      // Baru dibuka - mulai animasi masuk
      setIsVisible(true);
      setIsClosing(false);
    } else if (!isOpen && prevIsOpen.current) {
      // Baru ditutup - mulai animasi keluar
      setIsClosing(true);
      const timer = setTimeout(() => {
        setIsVisible(false);
        setIsClosing(false);
      }, ANIMATION_DURATION);
      return () => clearTimeout(timer);
    }
    prevIsOpen.current = isOpen;
  }, [isOpen]);

  // Jangan render jika tidak visible
  if (!isVisible && !isOpen) {
    return null;
  }

  // Handle close dengan animasi
  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      closeSubMenu();
    }, ANIMATION_DURATION);
  };

  // Handle klik item leaf - buka halaman dan tutup SubMenuBar
  const handleLeafClick = (item: MenuConfigItem) => {
    if (!isMenuLeaf(item)) return;

    const page = getPageByPath(item.path);
    if (page) {
      // Buka tab
      openTab({
        id: page.id,
        label: page.label,
        path: page.path,
        icon: page.icon,
      });
      // Navigate ke halaman
      navigate(item.path);
    }
    // Tutup SubMenuBar dengan animasi
    handleClose();
  };

  // Handle klik item branch - navigasi ke sub-level
  const handleBranchClick = (item: MenuConfigItem) => {
    if (!isMenuBranch(item)) return;
    navigateToChild(item.id, item.label, item.children);
  };

  // Handle klik item (auto detect leaf/branch)
  const handleItemClick = (item: MenuConfigItem) => {
    if (isMenuLeaf(item)) {
      handleLeafClick(item);
    } else if (isMenuBranch(item)) {
      handleBranchClick(item);
    }
  };

  // Handle klik overlay di luar bar
  const handleOverlayClick = () => {
    handleClose();
  };

  return (
    <>
      {/* Overlay - klik di luar untuk tutup */}
      <div
        className={`sub-menu-overlay ${isClosing ? 'closing' : ''}`}
        onClick={handleOverlayClick}
      />

      {/* SubMenuBar */}
      <div className={`sub-menu-bar ${isClosing ? 'closing' : ''}`}>
        {/* Left side: Back button + Breadcrumb */}
        <div className="sub-menu-left">
          {/* Back button - hanya tampil jika bisa back */}
          {canGoBack && (
            <button
              className="sub-menu-back"
              onClick={navigateBack}
              title="Kembali"
            >
              <ArrowLeft size={16} />
            </button>
          )}

          {/* Breadcrumb */}
          <div className="sub-menu-breadcrumb">
            {breadcrumb}
          </div>

          {/* Separator */}
          <div className="sub-menu-separator" />
        </div>

        {/* Center: Menu items */}
        <div className="sub-menu-items">
          {currentItems.map((item) => {
            const isBranch = isMenuBranch(item);
            const colorClass = getMenuColorClass(item.id);

            return (
              <button
                key={item.id}
                className={`sub-menu-item ${isBranch ? 'has-children' : ''} ${colorClass}`}
                onClick={() => handleItemClick(item)}
                title={item.label}
              >
                {item.icon && <span className="sub-menu-item-icon">{item.icon}</span>}
                <span className="sub-menu-item-label">{item.label}</span>
                {isBranch && <ChevronRight size={14} className="sub-menu-item-arrow" />}
              </button>
            );
          })}
        </div>

        {/* Right side: Close button */}
        <div className="sub-menu-right">
          <button
            className="sub-menu-close"
            onClick={handleClose}
            title="Tutup"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </>
  );
}
